import { type ComponentProps, useRef, useState } from "react";
import Image from "next/image";
import { remiLogger } from "@remi/logging";

import { useEffect } from "../../hooks/useEffect";
import { imageSkeleton, toBase64 } from "../../utils/image-placeholder";
import { RemiImageNotFound } from "./RemiImageNotFound";

export type RemiImageProps = ComponentProps<typeof Image> & {
  maxRetries?: number;
  /** Render the pulse skeleton instead of the broken-file icon on load failure. Use for
   *  images known to be mid-pipeline (upload/conversion pending), where failure is expected
   *  and transient — the icon reads as "upload failed" to users. */
  skeletonOnError?: boolean;
};

// Spaced retries let transient failures (upload still landing in S3) clear; the not-found
// icon is deliberately non-terminal — it reads as "upload failed" even when the image
// arrives seconds later (NSYNC-6950) — so slow recovery re-tries before it goes permanent.
const RETRY_DELAYS_MS = [2_000, 5_000, 10_000];
const RECOVERY_DELAYS_MS = [10_000, 30_000, 60_000];

const getMetricSrc = (src: RemiImageProps["src"]): string => {
  if (typeof src !== "string") {
    return "static-import";
  }
  const queryIndex = src.indexOf("?");
  return queryIndex === -1 ? src : src.slice(0, queryIndex);
};

export const RemiImage = ({ maxRetries = 3, skeletonOnError = false, ...props }: RemiImageProps) => {
  const [prevSrc, setPrevSrc] = useState(props.src);
  const [isError, setIsError] = useState(false);
  const [retries, setRetries] = useState(0);
  const [isAwaitingRetry, setIsAwaitingRetry] = useState(false);
  const [recoveryAttempts, setRecoveryAttempts] = useState(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  if (props.src !== prevSrc) {
    setPrevSrc(props.src);
    setRetries(0);
    setIsError(false);
    setIsAwaitingRetry(false);
    setRecoveryAttempts(0);
  }

  useEffect(() => {
    return () => clearTimeout(retryTimerRef.current);
  }, [props.src]);

  const getImageSrc = () => {
    if (retries === 0 || typeof props.src !== "string") {
      return props.src;
    }
    const joiner = props.src.includes("?") ? "&" : "?";
    return `${props.src}${joiner}_r=${retries}`;
  };

  if (isError || !props.src) {
    if (skeletonOnError) {
      const { onLoad: _onLoad, onError: _onError, ...skeletonProps } = props;
      return <Image {...skeletonProps} src={toBase64(imageSkeleton)} />;
    }
    return <RemiImageNotFound {...props} />;
  }

  if (isAwaitingRetry) {
    const { onLoad: _onLoad, onError: _onError, ...skeletonProps } = props;
    return <Image {...skeletonProps} src={toBase64(imageSkeleton)} />;
  }

  return (
    <Image
      {...props}
      src={getImageSrc()}
      placeholder={toBase64(imageSkeleton)}
      onError={(event) => {
        if (typeof props.src !== "string") {
          setIsError(true);
          props.onError?.(event);
          return;
        }

        if (retries < maxRetries) {
          const delay = RETRY_DELAYS_MS[Math.min(retries, RETRY_DELAYS_MS.length - 1)] ?? 2_000;
          setIsAwaitingRetry(true);
          retryTimerRef.current = setTimeout(() => {
            setIsAwaitingRetry(false);
            setRetries((r) => r + 1);
          }, delay);
          return;
        }

        setIsError(true);
        if (recoveryAttempts === 0) {
          remiLogger.captureCounterMetric("image.load-failed", 1, {
            attributes: { src: getMetricSrc(props.src) },
          });
          props.onError?.(event);
        }

        const recoveryDelay = RECOVERY_DELAYS_MS[recoveryAttempts];
        if (recoveryDelay !== undefined) {
          retryTimerRef.current = setTimeout(() => {
            setRecoveryAttempts((a) => a + 1);
            setRetries((r) => r + 1);
            setIsError(false);
          }, recoveryDelay);
        }
      }}
    />
  );
};
